/**
 * The sites in this workspace.
 *
 * Each card carried five separate colour scales: a citation tile that was a
 * gradient when it had data, dashed accent when it did not and cream when it
 * was locked; a green/amber/red breakdown line; a coloured score ring; a
 * business-profile chip in sky blue, amber or grey; and an amber left border
 * for drift. None of it agreed with any of the rest, and a person scanning
 * four of these could not tell which colour was the one to act on.
 *
 * The worst of it was at the bottom of the locked state: a blurred "82/100"
 * under the word "Preview", shown to somebody who has never had a score, at
 * the exact moment they are asked to pay. It was nobody's number. It is gone,
 * and what replaces it is what the check actually does.
 */

import React, { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronDown, Plus, ShieldCheck } from 'lucide-react'
import { cn } from '@/utils/cn'
import { getBusinessProfileStatusForDomain, type BusinessProfileStatus } from '@/utils/businessProfileStatus'
import { groupDomainsByRoot } from '@/utils/domain'
import { formatRelativeTime } from '@/utils/freshness'
import AddDomainDialog from '@/components/domains/AddDomainDialog'
import DomainFavicon from '@/components/DomainFavicon'
import { IntegrationIcon } from '@/components/IntegrationBadge'
import { FeatureUnlockModal, type UnlockFeatureKey } from '@/components/upgrade/FeatureUnlockModal'
import { DsIsland } from '@/design-system/island'
import { Button, Card, EmptyState, Skeleton, Status } from '@/design-system/primitives'
import { Tag } from '@/design-system/marks'
import { Asked, Bars, Metric } from '@/design-system/appcards'
import { Spinner } from '@/design-system/feedback'
import type { Integration } from '@/hooks/useIntegrations'
import type { UnifiedDomain, DomainDetailSummary, DomainCitationSummary, Organization } from '@shared/types'
import { usePageSetupAccess } from '@/hooks/usePageSetupAccess'
import '@/design-system/appcards.css'
import '@/design-system/feedback.css'
import '@/design-system/dashboard.css'

const BILLING_PLANS_PATH = '/settings/billing?section=plans#subscription-plans'

export interface DomainMonitoringStatus {
  monitoringEnabled: boolean
  monitoredUrlCount: number
  openDriftEvents: number
  monitoringHealth: string | null
}

interface PortfolioGridProps {
  domains: UnifiedDomain[]
  summaries: Map<string, DomainDetailSummary>
  monitoringStatuses?: Map<string, DomainMonitoringStatus>
  isSummaryLoading: boolean
  isLoadingDomains: boolean
  isPro: boolean
  canAddDomain: boolean
  organizations?: Organization[]
  canManageBusinessProfile?: boolean
  getIntegrationsForDomain?: (domain: string) => Integration[]
  className?: string
}

export function PortfolioGrid({
  domains,
  summaries,
  monitoringStatuses,
  isSummaryLoading,
  isLoadingDomains,
  isPro,
  canAddDomain,
  organizations = [],
  canManageBusinessProfile = true,
  getIntegrationsForDomain,
  className,
}: PortfolioGridProps) {
  const navigate = useNavigate()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [expandedRoots, setExpandedRoots] = useState<Set<string>>(new Set())
  const groups = useMemo(() => groupDomainsByRoot(domains), [domains])
  const hasSingleDomain = domains.length === 1

  useEffect(() => {
    setExpandedRoots(new Set())
  }, [domains])

  const toggleRoot = (rootDomain: string) => {
    setExpandedRoots((prev) => {
      const next = new Set(prev)
      next.has(rootDomain) ? next.delete(rootDomain) : next.add(rootDomain)
      return next
    })
  }

  const shell = (children: React.ReactNode, head?: React.ReactNode) => (
    <DsIsland panel={false} ground={false} className={className}>
      <div data-testid="portfolio-grid">
        {/*
          The heading was "Your domains" under a monospace uppercase "PORTFOLIO"
          eyebrow. Two names for one list, and one of them is a word for money.
        */}
        <Card title="Your sites" head={head}>
          {children}
        </Card>
      </div>
    </DsIsland>
  )

  if (isLoadingDomains) {
    return shell(
      <div className="ds-dash__sites" data-testid="portfolio-domain-list">
        {[1, 2].map((i) => (
          <div key={i} className="ds-site">
            <div className="ds-site__head">
              <Skeleton width={36} height={36} />
              <span className="ds-site__id">
                <Skeleton width={140} height={16} />
                <div style={{ marginTop: 6 }}>
                  <Skeleton width={100} height={12} />
                </div>
              </span>
            </div>
            <div className="ds-site__body">
              <Skeleton width="100%" height={96} />
              <Skeleton width="100%" height={96} />
            </div>
            <div className="ds-site__foot">
              <Skeleton width={160} height={12} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (domains.length === 0) {
    return shell(
      <>
        <EmptyState
          title="No sites here yet"
          action={
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus aria-hidden="true" />
              Add a site
            </Button>
          }
        >
          Add one and we will read it the way an assistant would: what it says, what is missing,
          and whether anything is in the way.
        </EmptyState>
        {showAddDialog && (
          <AddDomainDialog isOpen={showAddDialog} onClose={() => setShowAddDialog(false)} />
        )}
      </>
    )
  }

  const cardFor = (domain: UnifiedDomain, extra: Partial<SiteCardProps> = {}) => (
    <SiteCard
      domain={domain}
      summary={summaries.get(domain.id)}
      monitoringStatus={monitoringStatuses?.get(domain.id)}
      isSummaryLoading={isSummaryLoading && !summaries.get(domain.id)}
      isPro={isPro}
      organizations={organizations}
      canManageBusinessProfile={canManageBusinessProfile}
      integrations={getIntegrationsForDomain?.(domain.domain) || []}
      onOpen={() => navigate(`/domains/${encodeURIComponent(domain.domain)}`)}
      {...extra}
    />
  )

  return shell(
    <>
      <div data-testid="portfolio-domain-list" className="ds-dash__sites">
        {groups.map((group) => {
          const hasSubdomains = group.subdomains.length > 0

          if (!group.root) {
            return (
              <React.Fragment key={`group-${group.rootDomain}`}>
                {group.subdomains.map((sub) => (
                  <div key={sub.id} className={cn(hasSingleDomain && 'ds-dash__only')}>
                    {cardFor(sub)}
                  </div>
                ))}
              </React.Fragment>
            )
          }

          if (!hasSubdomains) {
            return (
              <div key={group.root.id} className={cn(hasSingleDomain && 'ds-dash__only')}>
                {cardFor(group.root)}
              </div>
            )
          }

          const isExpanded = expandedRoots.has(group.rootDomain)
          return (
            <div key={group.root.id} className="ds-dash__group">
              {cardFor(group.root, {
                subdomainCount: group.subdomains.length,
                isExpanded,
                onToggleExpand: () => toggleRoot(group.rootDomain),
              })}
              {isExpanded &&
                group.subdomains.map((sub) => (
                  <div key={sub.id} className="ds-dash__sub">
                    {cardFor(sub, { isSubdomainCard: true })}
                  </div>
                ))}
            </div>
          )
        })}
      </div>

      {showAddDialog && (
        <AddDomainDialog isOpen={showAddDialog} onClose={() => setShowAddDialog(false)} />
      )}
    </>,
    canAddDomain ? (
      <Button variant="secondary" size="sm" onClick={() => setShowAddDialog(true)}>
        <Plus aria-hidden="true" />
        Add a site
      </Button>
    ) : undefined
  )
}

/* ─── One site ────────────────────────────────────────────────────────────── */

interface SiteCardProps {
  domain: UnifiedDomain
  summary: DomainDetailSummary | undefined
  monitoringStatus?: DomainMonitoringStatus
  isSummaryLoading: boolean
  isPro: boolean
  organizations: Organization[]
  canManageBusinessProfile: boolean
  integrations: Integration[]
  onOpen: () => void
  subdomainCount?: number
  isExpanded?: boolean
  onToggleExpand?: () => void
  isSubdomainCard?: boolean
}

function SiteCard({
  domain,
  summary,
  monitoringStatus,
  isSummaryLoading,
  isPro,
  organizations,
  canManageBusinessProfile,
  integrations,
  onOpen,
  subdomainCount,
  isExpanded,
  onToggleExpand,
  isSubdomainCard,
}: SiteCardProps) {
  const navigate = useNavigate()
  const [unlockFeature, setUnlockFeature] = useState<UnlockFeatureKey | null>(null)

  const changed = domain.openDriftEvents ?? 0
  const hasDrift = changed > 0
  const profile = getBusinessProfileStatusForDomain(domain.domain, organizations)
  const linked = integrations.filter((i) => i.isLinked)
  const name = domain.displayName || domain.domain

  return (
    <div
      className="ds-site"
      data-attention={hasDrift || undefined}
      data-testid={`portfolio-domain-card-${domain.domain}`}
      onClick={onOpen}
    >
      <div className="ds-site__head">
        <DomainFavicon domain={domain.domain} className="ds-dash__mark" />
        <span className="ds-site__id">
          <div className="ds-site__name">
            {/*
              The whole card has been clickable and nothing in it was a link, so
              a keyboard never reached a site and a screen reader was never told
              one existed. The name is the link; the card keeps its click.
            */}
            <Link to={`/domains/${encodeURIComponent(domain.domain)}`} onClick={(e) => e.stopPropagation()}>
              {name}
            </Link>
          </div>
          {domain.displayName && <div className="ds-site__host">{domain.domain}</div>}
        </span>
        <span
          data-testid={`domain-card-header-actions-${domain.domain}`}
          className="ds-dash__profilewrap"
        >
          <ProfileButton
            domainName={domain.domain}
            status={profile}
            canManageBusinessProfile={canManageBusinessProfile}
            onClick={(event) => {
              event.stopPropagation()
              navigate(`/domains/${encodeURIComponent(domain.domain)}/settings#business-profile`)
            }}
          />
        </span>
      </div>

      {isSummaryLoading ? (
        <div className="ds-site__body">
          <Skeleton width="100%" height={110} />
          <Skeleton width="100%" height={110} />
        </div>
      ) : (
        <div className="ds-site__body" data-testid={`domain-card-status-strip-${domain.domain}`}>
          <AnswersTile domain={domain} citation={summary?.citation} compact={isSubdomainCard} />
          {!isSubdomainCard && <ReadinessTile domain={domain} summary={summary} />}
        </div>
      )}

      {!isSubdomainCard && (
        <SiteFoot
          domain={domain}
          schema={summary?.schema}
          monitoringStatus={monitoringStatus}
          isPro={isPro}
          changed={changed}
          onMonitorUnlock={() => setUnlockFeature('monitoring')}
        />
      )}

      {unlockFeature && (
        <FeatureUnlockModal
          feature={unlockFeature}
          domainName={domain.domain}
          onClose={() => setUnlockFeature(null)}
          onUpgrade={() => {
            setUnlockFeature(null)
            navigate(BILLING_PLANS_PATH)
          }}
        />
      )}

      {subdomainCount != null && subdomainCount > 0 && (
        <div className="ds-site__body ds-dash__more">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand?.()
            }}
          >
            <ChevronDown
              aria-hidden="true"
              style={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
            />
            {isExpanded ? 'Hide' : 'Show'} {subdomainCount} subdomain
            {subdomainCount > 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {linked.length > 0 && (
        <div className="ds-site__body">
          <div className="ds-dash__conn">
            <span>Connected to</span>
            {linked.map((integ) => (
              <IntegrationIcon
                key={`${integ.platform}-${integ.connectionId}`}
                platform={integ.platform}
                isLinked={integ.isLinked}
                portalName={integ.portalName}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ProfileButton({
  domainName,
  status,
  canManageBusinessProfile,
  onClick,
}: {
  domainName: string
  status: BusinessProfileStatus
  canManageBusinessProfile: boolean
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}) {
  const isLinked = status.state !== 'missing'
  const detail =
    status.completenessScore !== null && isLinked
      ? `${status.completenessScore}% complete`
      : canManageBusinessProfile
        ? status.detail
        : 'Ask an admin'

  return (
    <button
      type="button"
      onClick={onClick}
      className="ds-dash__profile"
      aria-label={`Verified Business Profile for ${domainName}: ${status.label}${status.completenessScore !== null ? `, ${status.completenessScore}% complete` : ''}`}
      title={
        isLinked
          ? 'The official business details we write into this site’s markup'
          : canManageBusinessProfile
            ? 'Add the official business details we write into this site’s markup'
            : 'A workspace owner or admin can fill this in'
      }
    >
      {/*
        A shield when there is a profile, and nothing when there is not. The
        chip used to carry a status mark in both cases, which meant the missing
        state showed a bare dash that read as a typo.
      */}
      {isLinked && <ShieldCheck aria-hidden="true" />}
      <span>{status.label}</span>
      <span className="ds-faint">{detail}</span>
    </button>
  )
}

/* ─── How AI answers about this business ──────────────────────────────────── */

export type CitationHeroState =
  | { kind: 'locked-upgrade' }
  | { kind: 'locked-trial' }
  | { kind: 'scanning' }
  | { kind: 'failed'; canRun: boolean }
  | { kind: 'has-score' }
  | { kind: 'ready'; canRun: boolean }

export function resolveCitationHeroState(citation: DomainCitationSummary | undefined): CitationHeroState {
  if (!citation) return { kind: 'ready', canRun: false }

  if (citation.isLocked) {
    return citation.lockReason === 'free_trial_no_initial_scan'
      ? { kind: 'locked-trial' }
      : { kind: 'locked-upgrade' }
  }
  if (citation.status === 'calculating') return { kind: 'scanning' }
  if (citation.status === 'failed') return { kind: 'failed', canRun: Boolean(citation.quota?.allowed) }
  if (citation.hasData && citation.score !== null) return { kind: 'has-score' }
  return { kind: 'ready', canRun: Boolean(citation.quota?.allowed) }
}

function AnswersTile({
  domain,
  citation,
  compact,
}: {
  domain: UnifiedDomain
  citation: DomainCitationSummary | undefined
  compact?: boolean
}) {
  const navigate = useNavigate()
  const state = resolveCitationHeroState(citation)
  const slug = encodeURIComponent(domain.domain)
  const go = (path: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(path)
  }

  const hasScore = state.kind === 'has-score'

  /* A subdomain shows the one number and nothing else. */
  if (compact) {
    return (
      <div
        className="ds-site__tile"
        data-testid={`domain-feature-tile-citation-${domain.domain}`}
      >
        <div className="ds-site__tileHead">
          <span className="ds-site__label">How AI answers about you</span>
          <span
            className="ds-site__when"
            data-testid={`domain-feature-score-citation-${domain.domain}`}
          >
            {hasScore && citation?.score != null ? `${citation.score} out of 100` : 'Not checked yet'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('ds-site__tile', !hasScore && 'ds-site__tile--quiet')}
      data-testid={`domain-feature-tile-citation-${domain.domain}`}
    >
      <div
        data-testid={`citation-summary-${domain.domain}`}
        aria-label={`AI recommendation summary for ${domain.domain}`}
      >
        <div className="ds-site__tileHead">
          <span className="ds-site__label">How AI answers about you</span>
          {hasScore && citation?.lastCheckedAt && (
            <span className="ds-site__when">{formatRelativeTime(citation.lastCheckedAt)}</span>
          )}
        </div>

        {state.kind === 'has-score' && (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span data-testid={`domain-feature-score-citation-${domain.domain}`}>
                <Metric value={citation!.score!} sm />
              </span>
              {citation!.grade && (
                <span data-testid={`domain-feature-grade-citation-${domain.domain}`}>
                  <Tag>{citation!.grade}</Tag>
                </span>
              )}
            </div>
            {/*
              The three counts were green, amber and red, which said that being
              mentioned is a warning and not being mentioned is an error. They
              are three facts about one question, so they are one sentence.
            */}
            <p className="ds-breakdown">
              <b>{citation!.counts.cited}</b> recommend you, <b>{citation!.counts.offering}</b>{' '}
              mention you, <b>{citation!.counts.notCited}</b> do not.
            </p>
            {/* Named, so the number means something: "71" is 71 out of what
                these three said, not a rating handed down from nowhere. */}
            <Asked questions={citation!.questionCount} />
            <div className="ds-dash__cta">
              <Button variant="secondary" size="sm" onClick={go(`/domains/${slug}/ai-recommendations`)}>
                See what they said
              </Button>
            </div>
          </>
        )}

        {(state.kind === 'ready' || state.kind === 'failed') && (
          <>
            <p className="ds-breakdown" style={{ marginTop: 0 }}>
              {state.kind === 'failed'
                ? 'The last check did not finish. Nothing is wrong with your site; you can run it again.'
                : `See whether ChatGPT, Claude and Gemini recommend ${domain.domain} when somebody asks. It takes about a minute.`}
            </p>
            {state.canRun ? (
              <div className="ds-dash__cta">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={go(`/domains/${slug}?startCitationScan=1#ai-recommendations`)}
                >
                  {state.kind === 'failed' ? 'Try it again' : 'Run AI Recommendations scan'}
                </Button>
              </div>
            ) : (
              <p className="ds-breakdown">
                {citation?.quota?.reason === 'monthly_limit_reached'
                  ? 'You have used this month’s checks. It resets with your billing month.'
                  : 'Included in your plan.'}
              </p>
            )}
          </>
        )}

        {state.kind === 'scanning' && (
          <div className="ds-dash__wait">
            <Spinner quiet />
            <p className="ds-breakdown" style={{ margin: 0 }}>
              Asking ChatGPT, Claude and Gemini about your business. This takes about a minute.
            </p>
          </div>
        )}

        {state.kind === 'locked-trial' && (
          <p className="ds-breakdown" style={{ marginTop: 0 }}>
            This appears once your first report finishes.
          </p>
        )}

        {state.kind === 'locked-upgrade' && (
          <>
            {/*
              This used to end with a blurred "82/100" labelled Preview. It was
              not this site's score, or anybody's: it was a number chosen to
              look good, shown to somebody at the moment they are asked to pay.
              What the check does is the honest version of the same pitch.
            */}
            <p className="ds-breakdown" style={{ marginTop: 0 }}>
              We ask ChatGPT, Claude and Gemini the questions your buyers ask, and tell you which
              of them recommend you, which only mention you, and which name somebody else.
              Included with any plan.
            </p>
            <div className="ds-dash__cta">
              <Button size="sm" onClick={go(BILLING_PLANS_PATH)}>
                See the plans
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ─── How ready the site itself is ────────────────────────────────────────── */

function ReadinessTile({
  domain,
  summary,
}: {
  domain: UnifiedDomain
  summary: DomainDetailSummary | undefined
}) {
  const navigate = useNavigate()
  const slug = encodeURIComponent(domain.domain)
  const ai = summary?.aiVisibility
  const hasScan = Boolean(domain.hasScanData && ai?.hasData)
  const scores = ai?.categoryScores

  const bars = scores
    ? [
        { label: 'Access', value: scores.access },
        { label: 'Content', value: scores.content },
        { label: 'Structure', value: scores.structure },
        { label: 'Schema', value: scores.schema },
        { label: 'Alternate', value: scores.alternate },
      ]
    : []

  return (
    <div
      className={cn('ds-site__tile', !hasScan && 'ds-site__tile--quiet')}
      data-testid={`domain-feature-tile-site-scan-${domain.domain}`}
    >
      <div
        data-testid={`site-scan-summary-${domain.domain}`}
        aria-label={`Site scan summary for ${domain.domain}`}
      >
        <div className="ds-site__tileHead">
          <span className="ds-site__label">How ready your site is</span>
          {hasScan && ai?.overallScore != null && (
            <span className="ds-site__when">{ai.overallScore} out of 100</span>
          )}
        </div>

        {hasScan && bars.length > 0 ? (
          <Bars rows={bars} />
        ) : (
          <>
            <p className="ds-breakdown" style={{ marginTop: 0 }}>
              A check reads the site the way an assistant would: whether it can reach your pages,
              what they say, how they are laid out, and whether your details are on them.
            </p>
            <div className="ds-dash__cta">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/domains/${slug}/ai-report`)
                }}
              >
                Look at this site
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ─── What is set up, and what we are watching ────────────────────────────── */

function SiteFoot({
  domain,
  schema,
  monitoringStatus,
  isPro,
  changed,
  onMonitorUnlock,
}: {
  domain: UnifiedDomain
  schema: DomainDetailSummary['schema'] | undefined
  monitoringStatus?: DomainMonitoringStatus
  isPro: boolean
  changed: number
  onMonitorUnlock: () => void
}) {
  const { setupHref, setupLabel } = usePageSetupAccess()
  const navigate = useNavigate()
  const slug = encodeURIComponent(domain.domain)
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    fn()
  }

  const readyPages = schema?.managedUrls || schema?.withSuperSchema || schema?.withAlternateFiles || 0
  const hasSchema = Boolean(schema?.hasData && readyPages > 0)
  const coverage = Math.max(0, Math.min(100, Math.round(schema?.coveragePercent ?? 0)))

  const monitoredPageCount = monitoringStatus?.monitoredUrlCount ?? 0
  const watching =
    isPro &&
    (monitoredPageCount > 0 ||
      Boolean(monitoringStatus?.monitoringEnabled) ||
      Boolean(domain.monitoringEnabled))

  return (
    <div className="ds-site__foot">
      <span>
        <span data-testid={`schema-coverage-summary-${domain.domain}`}>
          {hasSchema ? (
            <>
              <b>{readyPages.toLocaleString()} ready · {coverage}%</b> written
            </>
          ) : (
            <button
              type="button"
              className="ds-linkbtn"
              onClick={stop(() => navigate(setupHref(`/domains/${slug}/generate`)))}
            >
              {setupLabel('Set up schema')}
            </button>
          )}
        </span>
        {' · '}
        <span data-testid={`monitoring-summary-${domain.domain}`}>
          Monitor{' '}
          <b data-testid={`monitoring-state-${domain.domain}`}>{watching ? 'On' : 'Off'}</b>
          {!isPro ? (
            <>
              {' '}
              <button type="button" className="ds-linkbtn" onClick={stop(onMonitorUnlock)}>
                Unlock
              </button>
            </>
          ) : !watching ? (
            <>
              {' '}
              <button
                type="button"
                className="ds-linkbtn"
                onClick={stop(() => navigate('/monitoring/select'))}
              >
                Choose URLs
              </button>
            </>
          ) : null}
        </span>
        {changed > 0 && (
          <>
            {' · '}
            <Status kind="look" size="sm">
              {changed} changed
            </Status>
          </>
        )}
      </span>
      <span
        className="ds-site__go"
        aria-hidden="true"
        data-testid={`domain-card-footer-open-${domain.domain}`}
      >
        <ArrowRight />
      </span>
    </div>
  )
}
